import { useState, useCallback, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, KeyRound, ShieldAlert } from "lucide-react";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useModalA11y, dialogA11yProps } from "@/hooks/useModalA11y";
import { AuthShell } from "@/components/auth/AuthShell";
import { MfaSetup } from "@/components/auth/MfaSetup";
import { authApi } from "@/api/services";
import type { SocialProvidersResponse, LoginResponse } from "@/api/types";
import { setAccessToken, setRefreshToken } from "@/api/client";
import { displayNameOf, useAuthStore } from "@/stores/auth";
import { googleSignInFailure } from "@/lib/googleSignInFailure";

// ---------------------------------------------------------------------------
// Social sign-in SVG icons
// ---------------------------------------------------------------------------

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function AppleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Force Password Change Dialog
// ---------------------------------------------------------------------------

function ForcePasswordChangeDialog({
  email,
  onComplete,
}: {
  email: string;
  onComplete: () => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Blocking dialog (no dismiss): trap focus, lock scroll, no Escape.
  const dialogRef = useModalA11y<HTMLDivElement>({
    open: true,
    onClose: () => {},
    dismissable: false,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (newPassword.length < 12) {
      setError("New password must be at least 12 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      onComplete();
    } catch (err: unknown) {
      if (err && typeof err === "object" && "detail" in err) {
        setError((err as { detail: string }).detail);
      } else {
        setError("Failed to change password. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        {...dialogA11yProps({ labelledById: "fpc-title", describedById: "fpc-desc" })}
        className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-8 shadow-xl"
      >
        <div className="flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <ShieldAlert className="h-7 w-7 text-amber-600 dark:text-amber-400" />
          </div>
          <h2 id="fpc-title" className="mt-4 text-xl font-bold text-slate-900 dark:text-slate-100">
            Password Change Required
          </h2>
          <p id="fpc-desc" className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            Your password was set by an administrator. For security, you must
            choose a new password before continuing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fpc-current" required>
              Current password
            </Label>
            <Input
              id="fpc-current"
              type="password"
              placeholder="Enter the temporary password you received"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="fpc-new" required>
              New password
            </Label>
            <div className="relative">
              <Input
                id="fpc-new"
                type={showNew ? "text" : "password"}
                placeholder="Choose a strong password (12+ chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {newPassword && newPassword.length < 12 && (
              <p className="text-xs text-amber-600">
                Must be at least 12 characters
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="fpc-confirm" required>
              Confirm new password
            </Label>
            <Input
              id="fpc-confirm"
              type="password"
              placeholder="Re-enter your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {confirmPassword && confirmPassword !== newPassword && (
              <p className="text-xs text-red-600">Passwords do not match</p>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-error/30 bg-error/5 px-4 py-3">
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            size="lg"
            disabled={
              saving ||
              !currentPassword ||
              newPassword.length < 12 ||
              newPassword !== confirmPassword
            }
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Changing password...
              </>
            ) : (
              <>
                <KeyRound className="h-4 w-4" />
                Change Password & Continue
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login Page
// ---------------------------------------------------------------------------

/**
 * Where to land after a successful login. Honors a `?next=<path>` param so a
 * user who was bounced to /login from a deep link (e.g. a meeting room whose
 * session had lapsed) returns to exactly where they were headed. Only internal
 * absolute paths are allowed (never `//host` or an external URL) to prevent an
 * open-redirect.
 */
function postLoginDest(): string {
  try {
    const next = new URLSearchParams(window.location.search).get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  } catch {
    // ignore malformed query
  }
  return "/";
}

/** Where to land, honouring a plan chosen on /pricing before signing up.
 *
 *  ROW f8a46a1b. Verification is on in production, so registration returns
 *  verification_required and Register.tsx returns early — the navigate() there
 *  that carried the plan to /billing is dead for every real signup. The
 *  customer reads their email, often on another device, and arrives HERE. This
 *  is the first moment the choice can still be honoured.
 *
 *  `next` STILL WINS. Someone deep-linked to a page and was bounced through
 *  login is trying to get to that page, not to billing.
 *
 *  It only highlights. Billing.tsx treats ?plan= as a highlight and never starts
 *  a checkout from it, which the row requires: the customer has not clicked a
 *  priced control since /pricing.
 */
function landingFor(intent: string | null | undefined): string {
  const dest = postLoginDest();
  if (dest !== "/") return dest;
  const plan = (intent ?? "").trim();
  return plan ? `/billing?plan=${encodeURIComponent(plan)}` : dest;
}

/** Navigate, then forget the choice.
 *
 *  ONE helper for all three login paths, deliberately: password, social and
 *  post-password-change all land through here, and fixing one of three would
 *  leave a customer's plan surviving or not depending on how they signed in.
 *
 *  CONSUMED AFTER NAVIGATING. An intent never cleared diverts every future
 *  login to billing — helpful on day one, a bug by day three. Cleared too early
 *  and a failed navigation loses it silently, which is the same disappearing
 *  act this row is about. A failed consume costs one extra visit, nothing more,
 *  so the error is swallowed on purpose.
 */
function landAfterLogin(
  navigate: (to: string) => void,
  intent: string | null | undefined,
): void {
  const dest = landingFor(intent);
  navigate(dest);
  if (dest.startsWith("/billing?plan=")) {
    void authApi.consumePlanIntent().catch(() => undefined);
  }
}

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);
  // MFA second-factor step: challenge token (enrolled user) or mandatory enrolment.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaMandatory, setMfaMandatory] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaSubmitting, setMfaSubmitting] = useState(false);

  // Social providers configuration (fetched from backend)
  const [socialProviders, setSocialProviders] = useState<SocialProvidersResponse | null>(null);

  useEffect(() => {
    authApi.socialProviders().then(setSocialProviders).catch((err) => {
      console.warn("Failed to fetch social providers from API:", err);
      // Fallback: check build-time env vars so buttons still show
      // even if the API endpoint is temporarily unavailable
      const viteGoogleId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
      const viteAppleId = import.meta.env.VITE_APPLE_CLIENT_ID;
      if (viteGoogleId || viteAppleId) {
        setSocialProviders({
          google: { enabled: !!viteGoogleId, client_id: viteGoogleId || null },
          apple: { enabled: !!viteAppleId },
        });
      }
    });
  }, []);

  // Field-level validation errors
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});

  function validate(): boolean {
    const errors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      errors.email = "Please enter a valid email address.";
    }

    if (!password) {
      errors.password = "Password is required.";
    } else if (password.length < 8) {
      errors.password = "Password must be at least 8 characters.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  // Shared completion for every path that yields a real session (password,
  // post-MFA challenge, and mandatory-enrolment). Sets tokens, loads the user,
  // and routes — so the MFA gate cannot be sidestepped by a path that forgets it.
  const completeLogin = useCallback(
    async (accessToken: string, refreshToken: string | undefined, forcePw?: boolean) => {
      setAccessToken(accessToken);
      if (refreshToken) setRefreshToken(refreshToken);
      if (forcePw) {
        setShowForcePasswordChange(true);
        return;
      }
      const user = await authApi.me();
      const extended = user as unknown as { is_platform_admin?: boolean; id: string; org_id: string; roles?: string[]; signup_plan_intent?: string | null; display_name?: string; email?: string };
      useAuthStore.getState().setUser({
        id: user.id,
        name: displayNameOf(extended, email.trim()),
        email: extended.email || email.trim(),
        roles: user.roles ?? [],
        org_id: user.org_id,
        org_name: "",
        is_platform_admin: extended.is_platform_admin ?? false,
      });
      landAfterLogin(navigate, extended.signup_plan_intent);
    },
    [email, navigate],
  );

  async function handleMfaVerify(e: React.FormEvent) {
    e.preventDefault();
    setMfaError(null);
    if (!mfaToken) return;
    setMfaSubmitting(true);
    try {
      const res = await authApi.loginMfa({ mfa_token: mfaToken, code: mfaCode.trim() });
      await completeLogin(res.access_token ?? "", res.refresh_token, res.force_password_change);
    } catch (err: unknown) {
      if (err && typeof err === "object" && "detail" in err) {
        setMfaError((err as { detail: string }).detail);
      } else {
        setMfaError("That code didn't work. Try again, or use a recovery code.");
      }
    } finally {
      setMfaSubmitting(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      const loginResult = await authApi.login({
        email: email.trim(),
        password,
      }) as unknown as LoginResponse;

      // MFA-enrolled user → no session yet, collect the second factor.
      if (loginResult.mfa_required && loginResult.mfa_token) {
        setMfaToken(loginResult.mfa_token);
        setMfaMandatory(false);
        setIsSubmitting(false);
        return;
      }
      // Org enforces MFA + user not yet enrolled → mandatory enrolment. Stash
      // the enrol-scoped token as the bearer so MfaSetup can call setup/enable.
      if (loginResult.mfa_setup_required && loginResult.mfa_token) {
        setAccessToken(loginResult.mfa_token);
        setMfaMandatory(true);
        setIsSubmitting(false);
        return;
      }

      await completeLogin(
        loginResult.access_token ?? "",
        loginResult.refresh_token,
        loginResult.force_password_change,
      );
    } catch (err: unknown) {
      if (err && typeof err === "object" && "detail" in err) {
        setError((err as { detail: string }).detail);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleSocialLogin = useCallback(
    async (provider: "google" | "apple") => {
      setError(null);
      setSocialLoading(provider);

      try {
        let idToken: string | null = null;

        const googleSdk = window.google?.accounts?.id;
        const googleClientId = socialProviders?.google?.client_id
          || import.meta.env.VITE_GOOGLE_CLIENT_ID
          || "";

        if (provider === "google" && googleSdk) {
          if (!googleClientId) {
            throw new Error("Google sign-in is not configured. Please contact your administrator.");
          }
          // Google Identity Services: prompt one-tap or popup
          idToken = await new Promise<string>((resolve, reject) => {
            googleSdk.initialize({
              client_id: googleClientId,
              callback: (response: { credential: string }) => {
                resolve(response.credential);
              },
            });
            googleSdk.prompt((notification) => {
              // SAY WHICH FAILURE IT WAS. Google reports the reason on the
              // notification and this handler used to throw it away, so
              // `unregistered_origin` -- our site missing from the OAuth
              // client's authorised origins, which no customer can fix --
              // reached the user as "you cancelled". Row 7fe3d3db.
              const failure = googleSignInFailure(notification);
              if (failure) {
                reject(new Error(failure.message));
              }
            });
          });
        } else if (provider === "apple" && window.AppleID) {
          const appleResponse = await window.AppleID.auth.signIn();
          idToken = appleResponse.authorization.id_token;
        } else {
          throw new Error(
            `${provider === "google" ? "Google" : "Apple"} sign-in is not available. Please ensure the SDK is loaded.`
          );
        }

        if (!idToken) {
          throw new Error("Failed to obtain ID token");
        }

        const { access_token, refresh_token } = await authApi.socialLogin({
          id_token: idToken,
          provider,
        });

        setAccessToken(access_token);
        setRefreshToken(refresh_token);

        const user = await authApi.me();
        const ext = user as unknown as { is_platform_admin?: boolean; signup_plan_intent?: string | null; display_name?: string; email?: string };
        useAuthStore.getState().setUser({
          id: user.id,
          name: displayNameOf(ext),
          email: ext.email || "",
          roles: user.roles ?? [],
          org_id: user.org_id,
          org_name: "",
          is_platform_admin: ext.is_platform_admin ?? false,
        });

        landAfterLogin(navigate, ext.signup_plan_intent);
      } catch (err: unknown) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError("Social sign-in failed. Please try again.");
        }
      } finally {
        setSocialLoading(null);
      }
    },
    [navigate],
  );

  async function handlePasswordChanged() {
    // After password change, re-login with new token and proceed
    try {
      const user = await authApi.me();
      const extended = user as unknown as {
        is_platform_admin?: boolean;
        id: string;
        org_id: string;
        roles?: string[];
        signup_plan_intent?: string | null;
        display_name?: string;
        email?: string;
      };
      useAuthStore.getState().setUser({
        id: user.id,
        name: displayNameOf(extended, email.trim()),
        email: extended.email || email.trim(),
        roles: user.roles ?? [],
        org_id: user.org_id,
        org_name: "",
        is_platform_admin: extended.is_platform_admin ?? false,
      });
      landAfterLogin(navigate, extended.signup_plan_intent);
    } catch {
      // If re-auth fails, they'll need to login again with new password
      setShowForcePasswordChange(false);
      setPassword("");
      setError(
        "Password changed successfully. Please sign in with your new password.",
      );
    }
  }

  // Second-factor challenge (enrolled user finishing sign-in).
  if (mfaToken) {
    return (
      <AuthShell
        eyebrow="Security check"
        title="Enter your code"
        subtitle="Open your authenticator app and enter the 6-digit code — or use a recovery code."
      >
        <form onSubmit={handleMfaVerify} noValidate className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="mfa-code" required>Authentication code</Label>
            <Input
              id="mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="123456"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\s/g, ""))}
              variant={mfaError ? "error" : "default"}
              disabled={mfaSubmitting}
            />
            {mfaError && <p className="text-xs text-error">{mfaError}</p>}
          </div>
          <Button type="submit" className="w-full" disabled={mfaSubmitting || mfaCode.trim().length < 6}>
            {mfaSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Verify
          </Button>
          <button
            type="button"
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { setMfaToken(null); setMfaCode(""); setMfaError(null); }}
          >
            Back to sign in
          </button>
        </form>
      </AuthShell>
    );
  }

  // Mandatory enrolment (org enforces MFA, user not yet enrolled).
  if (mfaMandatory) {
    return (
      <AuthShell
        eyebrow="Security required"
        title="Set up two-factor authentication"
        subtitle="Your organization requires two-factor authentication before you can sign in."
      >
        <MfaSetup
          mode="mandatory"
          onEnabled={(resp) => {
            if (resp.access_token) {
              void completeLogin(resp.access_token, resp.refresh_token, false);
            }
          }}
        />
      </AuthShell>
    );
  }

  return (
    <>
      {showForcePasswordChange && (
        <ForcePasswordChangeDialog
          email={email}
          onComplete={handlePasswordChanged}
        />
      )}
    <AuthShell
      eyebrow="Welcome back"
      title="Sign in to FTL"
      subtitle="Continue managing your SDLC with Felix as your AI program manager."
      footer={
        <>
          Don&apos;t have an account?{" "}
          <Link
            to="/register"
            className="font-medium text-brand-600 hover:text-brand-700 transition-colors"
          >
            Sign up
          </Link>
        </>
      }
    >
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {/* Email field */}
            <div className="space-y-2">
              <Label htmlFor="email" required>
                Email address
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (fieldErrors.email) {
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }
                }}
                variant={fieldErrors.email ? "error" : "default"}
                disabled={isSubmitting}
              />
              {fieldErrors.email && (
                <p className="text-xs text-error">{fieldErrors.email}</p>
              )}
            </div>

            {/* Password field */}
            <div className="space-y-2">
              <Label htmlFor="password" required>
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (fieldErrors.password) {
                      setFieldErrors((prev) => ({
                        ...prev,
                        password: undefined,
                      }));
                    }
                  }}
                  variant={fieldErrors.password ? "error" : "default"}
                  disabled={isSubmitting}
                  className="pr-10"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="text-xs text-error">{fieldErrors.password}</p>
              )}
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-brand-600 hover:text-brand-700 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            {/* Server error */}
            {error && (
              <div className="rounded-lg border border-error/30 bg-error/5 px-4 py-3">
                <p className="text-sm text-error">{error}</p>
                {/* auth.py:657 refuses an unverified sign-in with a 403 whose
                    detail ends "...or request a new one." Nothing pointed at
                    where. /verify-email with no token renders exactly that
                    resend form (VerifyEmail.tsx, phase "no-token"), so the
                    sentence was describing a page the reader could not reach.
                    Matched on the server's own wording rather than a status
                    code, because the same 403 carries other refusals. */}
                {/verify your email/i.test(error) && (
                  <p className="mt-2 text-sm">
                    <Link
                      to="/verify-email"
                      data-testid="resend-verification"
                      className="font-semibold text-brand-600 hover:text-brand-700 hover:underline"
                    >
                      Send me a new verification link
                    </Link>
                  </p>
                )}
              </div>
            )}

            {/* Submit button */}
            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isSubmitting}
              data-testid="login-submit"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign in"
              )}
            </Button>
          </form>

          {/* Social login buttons — only shown when at least one provider is configured */}
          {socialProviders && (socialProviders.google?.enabled || socialProviders.apple?.enabled) && (
            <>
              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-200 dark:border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white dark:bg-slate-900 px-2 text-slate-500 dark:text-slate-400">Or continue with</span>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                {socialProviders.google?.enabled && (
                  <button
                    type="button"
                    onClick={() => handleSocialLogin("google")}
                    disabled={!!socialLoading || isSubmitting}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-50"
                  >
                    {socialLoading === "google" ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <GoogleIcon className="h-5 w-5" />
                    )}
                    Sign in with Google
                  </button>
                )}
                {socialProviders.apple?.enabled && (
                  <button
                    type="button"
                    onClick={() => handleSocialLogin("apple")}
                    disabled={!!socialLoading || isSubmitting}
                    className="flex w-full items-center justify-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-black px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-gray-900 disabled:opacity-50"
                  >
                    {socialLoading === "apple" ? (
                      <Loader2 className="h-5 w-5 animate-spin text-white" />
                    ) : (
                      <AppleIcon className="h-5 w-5" />
                    )}
                    Sign in with Apple
                  </button>
                )}
              </div>
            </>
          )}
    </AuthShell>
    </>
  );
}
